import { isVisitOpen, type Id } from '@odhaczka/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useState } from 'react';
import { db, getMeta } from '../db/db';
import type { VisitRow } from '../db/types';
import { getDeviceId } from '../db/clock';
import { currentUserId } from '../lib/auth';
import { memberFirstName, type HouseholdInfo } from './household';
import { buildMergeMap, resolveWith } from './products';

/** Ktoś jest teraz w sklepie z listą („Bartek w Auchan · 12/30 · 2 braki”). */
export interface ShoppingNow {
  /** Id użytkownika; wizyty sprzed wspólnego domu (bez userId) — `device:<deviceId>`. */
  userId: string;
  name: string;
  listId: Id;
  listTitle: string;
  storeId: Id;
  storeName: string;
  /** Pozycje już odhaczone (przez kogokolwiek). */
  done: number;
  total: number;
  /** Braki: nie było albo tylko część. */
  missing: number;
  isMe: boolean;
}

// Otwartość wizyty zależy od upływu czasu (2 h bez odhaczeń) — przeliczamy co minutę.
const TICK_MS = 60_000;

function useMinuteClock(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(t);
  }, []);
  return now;
}

async function loadShoppingNow(nowMs: number, onlyListId?: Id): Promise<ShoppingNow[]> {
  const visits = (onlyListId ? await db.visits.where('listId').equals(onlyListId).toArray() : await db.visits.toArray()).filter(
    (v) => !v.deleted && v.finishedMs === null,
  );
  if (visits.length === 0) return [];
  const listIds = [...new Set(visits.map((v) => v.listId))];
  const [lists, items, stores, household] = await Promise.all([
    db.lists.bulkGet(listIds),
    db.listItems.where('listId').anyOf(listIds).toArray(),
    db.stores.toArray(),
    getMeta<HouseholdInfo>('household'),
  ]);
  const listById = new Map(lists.filter((l) => !!l).map((l) => [l.id, l]));
  const itemsByList = new Map<Id, typeof items>();
  for (const i of items) {
    if (i.deleted) continue;
    const arr = itemsByList.get(i.listId);
    if (arr) arr.push(i);
    else itemsByList.set(i.listId, [i]);
  }
  const resolveStore = resolveWith(buildMergeMap(stores));
  const storeById = new Map(stores.map((s) => [s.id, s]));
  const members = new Map((household?.members ?? []).map((m) => [m.userId, m]));
  const me = currentUserId();
  const myDevice = getDeviceId();

  // Jedna pozycja na (osoba, lista): najświeższa otwarta wizyta.
  const latest = new Map<string, VisitRow>();
  for (const v of visits) {
    const list = listById.get(v.listId);
    if (!list || list.deleted || list.status !== 'active') continue;
    if (!isVisitOpen(v, itemsByList.get(v.listId) ?? [], nowMs)) continue;
    const key = `${v.userId ?? `device:${v.deviceId}`}|${v.listId}`;
    const prev = latest.get(key);
    if (!prev || v.startedMs > prev.startedMs) latest.set(key, v);
  }

  const out: ShoppingNow[] = [];
  for (const v of latest.values()) {
    const list = listById.get(v.listId)!;
    const listItems = itemsByList.get(v.listId) ?? [];
    const storeId = resolveStore(v.storeId);
    const isMe = v.userId ? v.userId === me : v.deviceId === myDevice;
    out.push({
      userId: v.userId ?? `device:${v.deviceId}`,
      name: memberFirstName(v.userId ? members.get(v.userId) : isMe && me ? members.get(me) : undefined),
      listId: v.listId,
      listTitle: list.title ?? '',
      storeId,
      storeName: storeById.get(storeId)?.name ?? '',
      done: listItems.filter((i) => i.status !== 'pending').length,
      total: listItems.length,
      missing: listItems.filter((i) => i.status === 'missing' || i.status === 'partial').length,
      isMe,
    });
  }
  return out.sort((a, b) => Number(a.isMe) - Number(b.isMe) || a.name.localeCompare(b.name, 'pl'));
}

/** Kto jest teraz w sklepie (wszystkie aktywne listy domu), także ja (isMe). */
export function useShoppingNow(): ShoppingNow[] | undefined {
  const now = useMinuteClock();
  return useLiveQuery(() => loadShoppingNow(now), [now]);
}

/** Kto robi zakupy z tą listą. */
export function useListShoppers(listId: Id | undefined): ShoppingNow[] | undefined {
  const now = useMinuteClock();
  return useLiveQuery(async () => (listId ? loadShoppingNow(now, listId) : []), [listId, now]);
}
